import { useCallback, useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useResponsive } from '@garnish/core';
import { useNoticeBannersStackActions } from '@garnish/overlays';

import { getUrqlError } from '@order/utils';

import { usePromoCodeTelemetry } from './usePromoCodeTelemetry';
import { useSubmitPromoCode } from './useSubmitPromoOrGiftCardCode';

export const usePromoCode = (refetchCredits?: () => void) => {
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const router = useRouter();
  const [promoCode, setPromoCode] = useState('');
  const [promoCodePreviouslyEntered, setPromoCodePreviouslyEntered] = useState(false);
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false);
  const [isPromoCodeAlreadyApplied, setIsPromoCodeAlreadyApplied] = useState(false);
  const routeName = usePathname();

  const { submitCodeResponse, submitCode } = useSubmitPromoCode();
  const { fetching: isSubmittingPromoCode } = submitCodeResponse;

  const {
    monitorPromoCodeApplied,
    monitorPromoCodeFailed,
    monitorPromoCodeEntered,
    monitorPromoCodeAttemptApply,
  } = usePromoCodeTelemetry();

  const handleSetPromoCode = useCallback(
    (input: string) => {
      setPromoCode(input);
      setPromoCodeInvalid(false);

      // Trigger promo code entered event once per session
      if (input.length > 0 && !promoCodePreviouslyEntered) {
        setPromoCodePreviouslyEntered(true);
        monitorPromoCodeEntered(routeName);
      }
    },
    [monitorPromoCodeEntered, promoCodePreviouslyEntered, routeName],
  );

  const submitPromoCode = useCallback(async () => {
    monitorPromoCodeAttemptApply(routeName);
    const { isSubmitSuccess, typename, error, isAlreadyApplied } = await submitCode(promoCode);

    setPromoCodeInvalid(!isSubmitSuccess);
    setIsPromoCodeAlreadyApplied(isAlreadyApplied);

    if (!isSubmitSuccess) {
      monitorPromoCodeFailed(
        formatMessage(messages.accountNoticeCreditInvalid),
        getUrqlError(error),
        routeName,
      );

      return;
    }

    setPromoCode('');
    setPromoCodePreviouslyEntered(false);
    monitorPromoCodeApplied(`${typename}`, routeName);

    if (currentBreakpoint.isXS) {
      router.back();
    }

    addNoticeBanner({
      text: formatMessage(
        typename === 'SubmitPromoCodeSuccessV2'
          ? messages.accountNoticePromoApplied
          : messages.accountNoticeCreditApplied,
      ),
      palette: 'success',
    });

    refetchCredits?.();
  }, [
    promoCode,
    currentBreakpoint.isXS,
    router,
    routeName,
    formatMessage,
    addNoticeBanner,
    monitorPromoCodeFailed,
    monitorPromoCodeApplied,
    monitorPromoCodeAttemptApply,
    refetchCredits,
    submitCode,
  ]);

  return {
    promoCode,
    isSubmittingPromoCode,
    isPromoCodeApplied: isPromoCodeAlreadyApplied,
    promoCodeInvalid,
    setPromoCode: handleSetPromoCode,
    submitPromoCode,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountNoticePromoApplied: {
    defaultMessage:
      'Your promo has been added as a reward. Apply it to an active order in your bag.',
    description: 'Promo Code | Notice | Promo Applied',
  },
  accountNoticeCreditApplied: {
    defaultMessage: 'Credit added successfully',
    description: 'Promo Code | Notice | Credit Applied',
  },
  accountNoticeCreditInvalid: {
    defaultMessage: 'This code or giftcard is not valid.',
    description: 'Account | Notice | Credit Invalid',
  },
});
